import React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const COMMAND_SNIPPET = `claude mcp add --transport http --scope user translations \\
  https://translator.ainur.app/mcp \\
  --header "Authorization: Bearer tr_..."`;

const CONFIG_SNIPPET = `{
  "mcpServers": {
    "translations": {
      "type": "http",
      "url": "https://translator.ainur.app/mcp",
      "headers": { "Authorization": "Bearer tr_..." }
    }
  }
}`;

const TOOLS = [
  ["translations_projects", "список проектов и языков, доступных ключу"],
  ["translations_search", "поиск по ключам и значениям"],
  ["translations_get", "значения одного ключа во всех языках"],
  ["translations_upsert", "создать или изменить перевод"],
  ["translations_delete", "удалить ключ из всех языков"],
];

const Snippet = ({ children }) => (
  <Box sx={{ position: "relative", mt: 1, mb: 2 }}>
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        pr: 14,
        borderRadius: 1,
        overflowX: "auto",
        fontSize: 13,
        fontFamily: "monospace",
        bgcolor: "action.hover",
      }}
    >
      {children}
    </Box>
    <Button
      size="small"
      sx={{ position: "absolute", top: 8, right: 8 }}
      onClick={() => navigator.clipboard.writeText(children)}
    >
      Скопировать
    </Button>
  </Box>
);

// Шаг: номер кружком слева, содержимое справа. minWidth: 0 обязателен — иначе
// сниппет с длинной командой распирает flex-строку вместо своего скролла.
const Step = ({ number, title, children }) => (
  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
    <Box
      sx={{
        width: 24,
        height: 24,
        flexShrink: 0,
        borderRadius: "50%",
        bgcolor: "primary.main",
        color: "primary.contrastText",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {number}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2">{title}</Typography>
      {children}
    </Box>
  </Box>
);

// Второй уровень: подробности, нужные не каждому. disableGutters + elevation={0}
// снимают у вложенного аккордеона карточную рамку — иначе внутри инструкции
// появляется коробка в коробке.
const Details = ({ title, children }) => (
  <Accordion
    disableGutters
    elevation={0}
    square
    sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
      <Typography variant="subtitle2">{title}</Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 0, pt: 0 }}>{children}</AccordionDetails>
  </Accordion>
);

// Инструкция лежит здесь, а не на главной: она нужна тому, кто уже пришёл за
// ключом. Содержимое обязано совпадать с
// docs/features/mcp-po-http-podklyuchenie-odnoi-komandoi-spec.md, §4.
//
// Аккордеон управляемый: на первом рендере список ключей ещё не загружен, и
// defaultExpanded вычислялся бы по пустому списку, а последующее значение
// неуправляемый компонент игнорирует — MUI на это ругается в консоль.
const McpGuide = ({ expanded, onToggle }) => (
  <Accordion
    id="mcp"
    expanded={expanded}
    onChange={(event, value) => onToggle(value)}
    sx={{ mb: 3 }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography variant="subtitle1">Подключение за 3 шага</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Step number={1} title="Выпустите ключ">
        <Typography variant="body2" color="text.secondary">
          Кнопка «Выпустить ключ» вверху страницы. Скопируйте токен сразу —
          второй раз он не показывается.
        </Typography>
      </Step>

      <Step number={2} title="Выполните команду">
        <Typography variant="body2" color="text.secondary">
          Один раз на машину, из любого каталога — клонировать{" "}
          <code>translator</code> не нужно. <code>--scope user</code> добавляет
          сервер в ваш личный конфиг, поэтому инструменты будут доступны во всех
          проектах сразу. Подставьте свой токен вместо <code>tr_...</code>.
        </Typography>
        <Snippet>{COMMAND_SNIPPET}</Snippet>
      </Step>

      <Step number={3} title="Перезапустите агентское окно">
        <Typography variant="body2" color="text.secondary">
          Инструменты появятся после перезапуска.
        </Typography>
      </Step>

      <Divider sx={{ my: 2 }} />

      <Details title="Агент без команды claude">
        <Typography variant="body2" color="text.secondary">
          Cursor, Codex и прочие клиенты с поддержкой MCP по HTTP настраиваются
          тем же адресом и заголовком:
        </Typography>
        <Snippet>{CONFIG_SNIPPET}</Snippet>
      </Details>

      <Details title="Что умеет агент">
        <Box component="ul" sx={{ pl: 3, m: 0 }}>
          {TOOLS.map(([name, description]) => (
            <Typography
              key={name}
              component="li"
              variant="body2"
              color="text.secondary"
            >
              <code>{name}</code> — {description}
            </Typography>
          ))}
        </Box>
      </Details>

      <Alert severity="info" sx={{ mt: 2 }}>
        Агенту доступны те же проекты и языки, что и вам: отдельно ничего
        настраивать не надо. Запись двухшаговая — без <code>confirm: true</code>{" "}
        инструмент возвращает список изменений и ничего не пишет. Отзыв ключа в
        списке ниже действует немедленно.
      </Alert>
    </AccordionDetails>
  </Accordion>
);

export default McpGuide;
